import { createFileRoute, useNavigate } from "../lib/router-compat";
import { useEffect, useState } from "react";

const AUDIO_CACHE = new Map<string, HTMLAudioElement>();

function getAudioElement(src: string) {
  const existing = AUDIO_CACHE.get(src);
  if (existing) return existing;

  const audio = new Audio(src);
  audio.preload = "auto";
  AUDIO_CACHE.set(src, audio);
  return audio;
}

function preloadAudio(src: string) {
  if (typeof document === "undefined") return () => {};

  const existing = document.head.querySelector(`link[rel="preload"][as="audio"][href="${src}"]`);
  if (existing) return () => {};

  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "audio";
  link.href = src;
  document.head.appendChild(link);

  return () => {
    if (link.parentNode) link.parentNode.removeChild(link);
  };
}

function waitForAudioLoad(src: string) {
  return new Promise<void>((resolve) => {
    const audio = getAudioElement(src);

    if (audio.readyState >= 4) {
      resolve();
      return;
    }

    const done = () => {
      cleanup();
      resolve();
    };
    const cleanup = () => {
      audio.removeEventListener("canplaythrough", done);
      audio.removeEventListener("loadeddata", done);
      audio.removeEventListener("error", done);
    };

    audio.addEventListener("canplaythrough", done, { once: true });
    audio.addEventListener("loadeddata", done, { once: true });
    audio.addEventListener("error", done, { once: true });
    audio.load();
  });
}

export const Route = createFileRoute("/learn-to-play")({
  head: () => ({
    meta: [
      { title: "Learn to Play Steel Pan — Education" },
      { name: "description", content: "Explore bass, tenor, and cello steel pan notes in an interactive lesson layout." },
    ],
  }),
  component: EducationPage,
});


function EducationPage() {
  const navigate = useNavigate();
  const [currentPan, setCurrentPan] = useState<'tenor' | 'cello' | 'bass'>('tenor');
  const [showEnsemble, setShowEnsemble] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioLoadProgress, setAudioLoadProgress] = useState({ loaded: 0, total: 0 });
  const [audioLoadErrorCount, setAudioLoadErrorCount] = useState(0);

  const assetBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

  // Properly hide the site shell (SiteNav + SiteFooter) for this page
  // Previous CSS selectors failed because SiteNav renders *before* this component in the DOM.
  useEffect(() => {
    document.body.classList.add('tutorial-page');
    return () => document.body.classList.remove('tutorial-page');
  }, []);

  useEffect(() => {
    const samples = [
      ...Object.values(TENOR_C_V2_INNER),
      ...Object.values(TENOR_C_V2_CENTER),
      ...Object.values(TENOR_C_V2_OUTER_SPECIAL),
      ...Object.values(CELLO_V2_SAMPLES),
      ...Object.values(BASS_V2_SAMPLES),
      "outer-c.mp3",
      "outer-d.mp3",
      "outer-e.mp3",
      "outer-f.mp3",
      "outer-fsharp.mp3",
      "outer-g.mp3",
      "outer-a.mp3",
      "outer-b.mp3",
      "outer-aflat.mp3",
      "outer-bflat.mp3",
      "outer-csharp.mp3",
      "outer-eflat.mp3",
      "c-tenor-low-c.mp3",
      "c-tenor-low-csharp.mp3",
      "tenor-c-outer-c.mp3",
      "tenor-c-outer-csharp.mp3",
    ];

    const assetRoot = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const unique = Array.from(new Set(samples.map((sample) => `${assetRoot}/tutorial_v2/sounds/${sample}`)));
    const cleanups = unique.map((src) => preloadAudio(src));
    let cancelled = false;

    setAudioReady(false);
    setAudioLoadProgress({ loaded: 0, total: unique.length });
    setAudioLoadErrorCount(0);

    (async () => {
      let loaded = 0;
      let errorCount = 0;

      await Promise.all(
        unique.map(async (src) => {
          try {
            await waitForAudioLoad(src);
          } catch {
            errorCount += 1;
          } finally {
            loaded += 1;
            if (!cancelled) {
              setAudioLoadProgress({ loaded, total: unique.length });
              setAudioLoadErrorCount(errorCount);
            }
          }
        }),
      );

      if (!cancelled) {
        setAudioReady(true);
      }
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  async function enableAudio() {
    try {
      const AudioCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtor) {
        const ctx = new AudioCtor();
        await ctx.resume();
        (window as any).__audioCtx = ctx;
      }
      setAudioEnabled(true);
    } catch (e) {
      console.warn('Audio enable failed', e);
    }
  }

  const pans = [
    { key: 'tenor' as const, label: 'Tenor C', img: `${assetBase}/tutorial_v2/images/pan-tenor-c.png` },
    { key: 'cello' as const, label: 'Cello', img: `${assetBase}/tutorial_v2/images/pan-cello-w.jpg` },
    { key: 'bass' as const, label: 'Bass', img: `${assetBase}/tutorial_v2/images/pan-bass-w.jpg` },
  ];

  return (
    <div className="learn-to-play min-h-screen bg-[#0e0a08] text-white overflow-hidden">
      <style>{css}</style>

      {!audioReady && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0b0706]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,214,153,0.12),transparent_50%),radial-gradient(circle_at_top,rgba(132,90,38,0.22),transparent_45%)]" />
          <div className="relative w-[min(92vw,28rem)] rounded-[28px] border border-white/10 bg-white/5 px-6 py-7 text-center shadow-[0_25px_80px_rgba(0,0,0,0.5)] backdrop-blur-md">
            <div className="mx-auto mb-4 h-14 w-14 rounded-full border border-white/15 border-t-[#ffd699] animate-spin" />
            <div className="font-serif text-3xl tracking-[-0.02em]">Loading sounds</div>
            <div className="mt-2 text-sm text-white/70">
              {audioLoadProgress.loaded}/{audioLoadProgress.total} audio files ready
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#ffd699] via-[#f3b86b] to-[#d58b33] transition-all duration-200"
                style={{
                  width: audioLoadProgress.total
                    ? `${Math.round((audioLoadProgress.loaded / audioLoadProgress.total) * 100)}%`
                    : "0%",
                }}
              />
            </div>
            {audioLoadErrorCount > 0 && (
              <div className="mt-3 text-xs text-white/45">
                {audioLoadErrorCount} file{audioLoadErrorCount === 1 ? "" : "s"} failed to preload, but the page will still continue.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating close only — v2 standalone feel, no site chrome */}
      <button
        onClick={() => navigate("/")}
        className="tutorial-close"
        aria-label="Close tutorial"
        disabled={!audioReady}
        style={!audioReady ? { pointerEvents: "none", opacity: 0.35 } : undefined}
      >
        ×
      </button>

      <main className={`relative min-h-screen flex flex-col items-center md:justify-center justify-start px-4 md:px-8 pt-4 pb-12 ${audioReady ? "" : "pointer-events-none select-none blur-[1px]"}`}>
          {/* v2-style header */}
          <div className="header text-center mb-6">
            <h1 className="font-serif text-[clamp(28px,5vw,44px)] tracking-[-0.02em]">
              {pans.find(p => p.key === currentPan)?.label || 'Steelpan'}<span className="dot">.</span>
            </h1>
            <div className="controls mt-3">
              <button
                onClick={enableAudio}
                className="btn"
              >
                {audioEnabled ? 'Sound on' : 'Sound'}
              </button>
              <button
                onClick={() => {
                  setShowEnsemble(!showEnsemble);
                  if (showEnsemble) setCurrentPan('tenor');
                }}
                className={`btn hidden md:inline-flex ${showEnsemble ? 'active' : ''}`}
              >
                {showEnsemble ? 'Solo' : 'All'}
              </button>
            </div>
          </div>

          {/* Top menu: Tenor / Cello / Bass + All — placed right under the header controls on mobile */}
          <div className="pan-top-menu md:hidden mb-4">
            {pans.map((pan) => (
              <button
                key={pan.key}
                onClick={() => {
                  setCurrentPan(pan.key);
                  setShowEnsemble(false);
                }}
                className={!showEnsemble && currentPan === pan.key ? 'active' : ''}
              >
                <span className="pt-thumb"><img src={pan.img} alt="" /></span>
                {pan.label}
              </button>
            ))}
            <button
              onClick={() => setShowEnsemble(true)}
              className={showEnsemble ? 'active' : ''}
            >
              All
            </button>
          </div>

          {/* THE STAGE — exact v2 layout recreation */}
          {!showEnsemble ? (
            <div className="stage-wrap relative w-full max-w-[1024px] aspect-[16/10] flex items-center justify-center">
              {/* Decorative glow + rings (from v2) */}
              <div className="bg-deco">
                <div className="blob"></div>
                <div className="ring1"></div>
                <div className="ring2"></div>
              </div>

              {/* Corner satellite thumbnails — v2's primary nav (large, elegant) */}
              {pans.map((pan, idx) => {
                // Spread like v2: top-left, top-right, bottom-left (3 pans)
                const slots = [
                  { top: '7%', left: '5%' },
                  { top: '7%', right: '5%' },
                  { bottom: '9%', left: '5%' }
                ];
                const pos = slots[idx] || slots[0];
                const isActive = currentPan === pan.key;
                return (
                  <button
                    key={pan.key}
                    onClick={() => {
                      setCurrentPan(pan.key);
                      setShowEnsemble(false);
                    }}
                    className={`satellite ${isActive ? 'opacity-40' : ''}`}
                    style={pos}
                  >
                    <div className="satellite-img">
                      <img src={pan.img} alt={pan.label} />
                    </div>
                    <div className="satellite-label">{pan.label}</div>
                  </button>
                );
              })}

              {/* Central beautiful stage (tp-wrap) */}
              <div className="tp-wrap relative" style={{ width: 'min(460px, 68vw)' }}>
                <div className="tp-glow"></div>
                <div className="tp-img-stack">
                  {currentPan === 'tenor' && (
                    <img src={`${assetBase}/tutorial_v2/images/pan-tenor-c.png`} className="tp-img active" alt="Tenor C" />
                  )}
                  {currentPan === 'cello' && (
                    <img src={`${assetBase}/tutorial_v2/images/pan-cello-w.jpg`} className="tp-img active" alt="Cello" />
                  )}
                  {currentPan === 'bass' && (
                    <img src={`${assetBase}/tutorial_v2/images/pan-bass-w.jpg`} className="tp-img active" alt="Bass" />
                  )}
                </div>
                <div className="tp-shine"></div>

                {/* The actual playable notes — rendered by the Orbit components (they provide the positioned buttons) */}
                <div className="tp-notes absolute inset-0">
                  {currentPan === 'tenor' && <OrbitTenorCV2 embedded />}
                  {currentPan === 'cello' && <OrbitCelloV2 embedded />}
                  {currentPan === 'bass' && <OrbitBassV2 embedded />}
                </div>
              </div>
            </div>
          ) : (
            /* Ensemble view — clean, uniform, beautiful (secondary) */
            <div className="w-full max-w-5xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 text-center mb-4">Ensemble — play any note on any pan</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 place-items-center">
                {pans.map((pan) => (
                  <div key={pan.key} className="w-full max-w-[260px]">
                    <div className="text-center text-xs tracking-[0.2em] text-white/60 mb-2">{pan.label}</div>
                    <div className="tp-wrap relative aspect-square" style={{ width: '100%' }}>
                      <div className="tp-glow"></div>
                      <div className="tp-img-stack">
                        <img src={pan.img} className="tp-img active" alt={pan.label} />
                      </div>
                      <div className="tp-shine"></div>
                      <div className="tp-notes absolute inset-0">
                        {pan.key === 'tenor' && <OrbitTenorCV2 ensemble embedded />}
                        {pan.key === 'cello' && <OrbitCelloV2 ensemble embedded />}
                        {pan.key === 'bass' && <OrbitBassV2 ensemble embedded />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
  );
}

/* ==================== Tenor C from tutorial_v2 ==================== */

const TENOR_C_V2_LAYOUT = [
  {x:88.67,y:49,size:10},{x:84.33,y:68.17,size:10},{x:69.33,y:82.33,size:10},
  {x:49.67,y:88.33,size:10},{x:30.67,y:83.67,size:10},{x:17,y:69.33,size:10},
  {x:11.17,y:50.17,size:10},{x:18,y:31.83,size:10},{x:31.83,y:17.33,size:10},
  {x:50.5,y:12.83,size:10},{x:68.83,y:17.67,size:10},{x:83.33,y:31.17,size:10},
  {x:69.67,y:49,size:6},{x:68.67,y:59.67,size:6},{x:59.17,y:65.17,size:6},
  {x:49,y:70.5,size:6},{x:38.17,y:69.17,size:6},{x:32.83,y:59.67,size:6},
  {x:27,y:50.5,size:6},{x:33.67,y:40.83,size:6},{x:39.67,y:31.17,size:6},
  {x:49.17,y:27.33,size:6},{x:59.33,y:32.17,size:6},{x:68.83,y:38.67,size:6},
  {x:56,y:48.17,size:4},{x:51,y:54.83,size:4},{x:41.67,y:53.83,size:4},
  {x:44,y:46.17,size:4},{x:51.5,y:41.5,size:4}
];

// Note order for Tenor C (circle of 4ths starting from D)
const V2_NOTES = ["D","G","C","F","B♭","E♭","A♭","C♯","F♯","B","E","A"];

const TENOR_C_V2_INNER: Record<number, string> = {
  0:"tenor-inner-d.mp3",1:"tenor-inner-g.mp3",2:"tenor-inner-c.mp3",3:"tenor-inner-f.mp3",
  4:"tenor-inner-bflat.mp3",5:"tenor-inner-eflat.mp3",6:"tenor-inner-aflat.mp3",7:"tenor-inner-csharp.mp3",
  8:"tenor-inner-fsharp.mp3",9:"tenor-inner-b.mp3",10:"tenor-inner-e.mp3",11:"tenor-inner-a.mp3",
};

const TENOR_C_V2_CENTER: Record<number, string> = {
  0:"tenor-center-d.mp3",1:"tenor-center-f.mp3",2:"tenor-center-eflat.mp3",
  3:"tenor-center-fsharp.mp3",4:"tenor-center-e.mp3",
};

// Center slots that should use inner samples instead (exact from v2)
const TENOR_C_V2_CENTER_OVERRIDES: Record<number, {sampleIdx: number}> = {
  1: { sampleIdx: 2 },   // C using tenor-inner-c
  4: { sampleIdx: 7 },   // C# using tenor-inner-csharp
};

// Outer special samples for Tenor C (only C and C#)
const TENOR_C_V2_OUTER_SPECIAL: Record<number, string> = {
  2: "tenor-c-outer-c.mp3",
  7: "tenor-c-outer-csharp.mp3",
};

function getTenorCV2Sample(layoutIdx: number): string {
  if (layoutIdx < 12) {
    // Outer ring
    const noteIdx = layoutIdx; // for Tenor C ring map is identity
    if (TENOR_C_V2_OUTER_SPECIAL[noteIdx]) {
      return TENOR_C_V2_OUTER_SPECIAL[noteIdx];
    }
    const name = V2_NOTES[noteIdx].replace("♭", "flat").replace("♯", "sharp").toLowerCase();
    return `outer-${name}.mp3`;
  }
  if (layoutIdx < 24) {
    // Middle ring (Tenor C from v2)
    const noteIdx = layoutIdx - 12;
    // Special case from v2: for middle ring, C (2) and C# (7) use regular outer samples (not inner)
    if (noteIdx === 2 || noteIdx === 7) {
      const name = V2_NOTES[noteIdx].replace("♭", "flat").replace("♯", "sharp").toLowerCase();
      return `outer-${name}.mp3`;
    }
    return TENOR_C_V2_INNER[noteIdx] || "tenor-inner-c.mp3";
  }
  // Center notes (24-28)
  const centerSlot = layoutIdx - 24;
  const override = TENOR_C_V2_CENTER_OVERRIDES[centerSlot];
  if (override) {
    return TENOR_C_V2_INNER[override.sampleIdx] || "tenor-inner-c.mp3";
  }
  const centerIdx = [2,1,0,4,3][centerSlot] ?? 0;
  return TENOR_C_V2_CENTER[centerIdx] || "tenor-center-e.mp3";
}

function getNoteLabelForV2(layoutIdx: number): string {
  if (layoutIdx < 12) return V2_NOTES[layoutIdx];
  if (layoutIdx < 24) return V2_NOTES[layoutIdx - 12];
  const centerSlot = layoutIdx - 24;
  const override = TENOR_C_V2_CENTER_OVERRIDES[centerSlot];
  if (override) return V2_NOTES[override.sampleIdx];
  const centerIdx = [2,1,0,4,3][centerSlot] ?? 0;
  return ["D″","F″","E♭″","F♯″","E″"][centerIdx] || "C";
}

function OrbitTenorCV2({ ensemble = false, embedded = false }: { ensemble?: boolean; embedded?: boolean }) {
  const [hitId, setHitId] = useState<string | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [lastSample, setLastSample] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const assetBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

  async function playNote(layoutIdx: number) {
    const sampleFile = getTenorCV2Sample(layoutIdx);
    const fullPath = `${assetBase}/tutorial_v2/sounds/${sampleFile}`;
    setLastSample(sampleFile);
    setLastError(null);

    const audio = getAudioElement(fullPath);
    audio.volume = 0.92;

    try {
      await audio.play();
      setAudioUnlocked(true);
    } catch (e: any) {
      const msg = e?.message || String(e);
      console.error("Audio play failed:", fullPath, msg);
      setLastError(msg);

      // Try to resume any existing AudioContext (helps on some browsers)
      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = (window as any).__audioCtx || new Ctx();
        await ctx.resume();
        (window as any).__audioCtx = ctx;

        setTimeout(async () => {
          try {
            await audio.play();
            setAudioUnlocked(true);
            setLastError(null);
          } catch (e2: any) {
            setLastError(e2?.message || "Still blocked after resume");
          }
        }, 80);
      } catch (ctxErr) {
        setLastError("Audio blocked by browser (try tapping Enable Sound higher on the page first)");
      }
    }

    const id = `v2-${layoutIdx}`;
    setHitId(id);
    setTimeout(() => setHitId((h) => (h === id ? null : h)), 620);
  }

  // Embedded mode: parent (the v2 stage) provides the photo + tp-wrap frame. We only render the note pads.
  if (embedded) {
    return (
      <>
        {TENOR_C_V2_LAYOUT.map((p, i) => {
          const id = `v2-${i}`;
          const label = getNoteLabelForV2(i);
          const isHit = hitId === id;
          const sz = ensemble ? Math.max(p.size * 0.78, 6.5) : p.size;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`tp-note absolute ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${sz}%`,
                height: `${sz}%`,
                marginLeft: `-${sz / 2}%`,
                marginTop: `-${sz / 2}%`,
                fontSize: sz < 6 ? '8px' : '9px',
              }}
              aria-label={`Play ${label}`}
            >
              {label}
            </button>
          );
        })}
      </>
    );
  }

  // Standalone fallback
  const cw = ensemble ? 320 : 480;
  return (
    <div className="mx-auto" style={{ width: cw }}>
      <div className="relative aspect-square" style={{ width: cw }}>
        <img
          src={`${assetBase}/tutorial_v2/images/pan-tenor-c.png`}
          alt="Tenor C steelpan"
          className="absolute inset-0 w-full h-full rounded-full object-cover"
          style={{ boxShadow: "inset 0 12px 50px rgba(0,0,0,.75), inset 0 -30px 80px rgba(0,0,0,.6)" }}
        />
        {TENOR_C_V2_LAYOUT.map((p, i) => {
          const id = `v2-${i}`;
          const label = getNoteLabelForV2(i);
          const isHit = hitId === id;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`note absolute flex items-center justify-center rounded-full border text-[10px] font-semibold transition-all active:scale-[0.92] ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`, top: `${p.y}%`,
                width: `${p.size}%`, height: `${p.size}%`,
                marginLeft: `-${p.size / 2}%`, marginTop: `-${p.size / 2}%`,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ==================== Cello from tutorial_v2 ==================== */

const CELLO_V2_NOTES = [
  { name: "E♭",  freq: 155.56 },
  { name: "A",   freq: 220 },
  { name: "C",   freq: 130.81 },
  { name: "C′",  freq: 261.63 },
  { name: "F♯",  freq: 185 },
  { name: "E♭′", freq: 311.13 },
  { name: "F♯′", freq: 369.99 },
  { name: "A",   freq: 440 },
  { name: "C",   freq: 523.25 },
];

const CELLO_V2_LAYOUT = [
  { x: 72.02, y: 23.65, size: 26 },
  { x: 82.79, y: 59.62, size: 26 },
  { x: 51.83, y: 82.12, size: 26, w: 48, h: 28 },
  { x: 18.56, y: 65.19, size: 26, w: 32, h: 20 },
  { x: 19.13, y: 33.27, size: 26, w: 28, h: 22 },
  { x: 42,    y: 18,    size: 26, w: 22, h: 22, rot: 10 },
  { x: 46.44, y: 40,    size: 18, w: 16, h: 14 },
  { x: 60.29, y: 53.65, size: 18, w: 16, h: 14 },
  { x: 37.6,  y: 56.54, size: 18, w: 16, h: 14 },
];

const CELLO_V2_SAMPLES: Record<number, string> = {
  0: "cello-low-eflat.mp3",
  1: "cello-a.mp3",
  2: "cello-low-c.mp3",
  3: "cello-high-c.mp3",
  4: "cello-fsharp.mp3",
  5: "cello-high-eflat.mp3",
  6: "cello-inner-fsharp.mp3",
  7: "cello-inner-a.mp3",
  8: "cello-inner-c.mp3",
};

function OrbitCelloV2({ ensemble = false, embedded = false }: { ensemble?: boolean; embedded?: boolean }) {
  const [hitId, setHitId] = useState<string | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [lastSample, setLastSample] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const assetBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

  async function playNote(idx: number) {
    const sampleFile = CELLO_V2_SAMPLES[idx] || "cello-low-c.mp3";
    const fullPath = `${assetBase}/tutorial_v2/sounds/${sampleFile}`;
    setLastSample(sampleFile);
    setLastError(null);

    const audio = getAudioElement(fullPath);
    audio.volume = 0.9;

    try {
      await audio.play();
      setAudioUnlocked(true);
    } catch (e: any) {
      const msg = e?.message || String(e);
      console.error("Cello audio failed:", fullPath, msg);
      setLastError(msg);

      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = (window as any).__audioCtx || new Ctx();
        await ctx.resume();
        (window as any).__audioCtx = ctx;

        setTimeout(async () => {
          try {
            await audio.play();
            setAudioUnlocked(true);
            setLastError(null);
          } catch (e2: any) {
            setLastError(e2?.message || "Still blocked");
          }
        }, 80);
      } catch {}
    }

    const id = `cello-${idx}`;
    setHitId(id);
    setTimeout(() => setHitId((h) => (h === id ? null : h)), 550);
  }

  if (embedded) {
    // Only notes layer for the v2 stage treatment. Uniform small circles (user requirement).
    const NOTE_SIZE = ensemble ? 9.5 : 8.5;
    return (
      <>
        {CELLO_V2_LAYOUT.map((p, i) => {
          const id = `cello-${i}`;
          const note = CELLO_V2_NOTES[i];
          const isHit = hitId === id;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`tp-note absolute ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${NOTE_SIZE}%`,
                height: `${NOTE_SIZE}%`,
                marginLeft: `-${NOTE_SIZE / 2}%`,
                marginTop: `-${NOTE_SIZE / 2}%`,
                fontSize: '8px',
              }}
              aria-label={`Play ${note.name}`}
            >
              {note.name}
            </button>
          );
        })}
      </>
    );
  }

  // Standalone fallback
  const cw = ensemble ? 320 : 480;
  return (
    <div className="mx-auto" style={{ width: cw }}>
      <div className="relative aspect-square" style={{ width: cw }}>
        <img
          src={`${assetBase}/tutorial_v2/images/pan-cello-w.jpg`}
          alt="Cello steelpan"
          className="absolute inset-0 w-full h-full rounded-full object-cover"
          style={{ boxShadow: "inset 0 12px 45px rgba(0,0,0,.75), inset 0 -30px 75px rgba(0,0,0,.6)" }}
        />
        {CELLO_V2_LAYOUT.map((p, i) => {
          const id = `cello-${i}`;
          const note = CELLO_V2_NOTES[i];
          const isHit = hitId === id;
          const size = 9;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`absolute flex items-center justify-center rounded-full border text-[9px] font-medium transition-all active:scale-[0.92] ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`, top: `${p.y}%`,
                width: `${size}%`, height: `${size}%`,
                marginLeft: `-${size / 2}%`, marginTop: `-${size / 2}%`,
              }}
            >
              {note.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ==================== Bass from tutorial_v2 ==================== */

const BASS_V2_NOTES = [
  { name: "C₂", freq: 65.41 },
  { name: "G",  freq: 98 },
  { name: "C₃", freq: 130.81 },
];

const BASS_V2_LAYOUT = [
  { x: 69.71, y: 33.27, size: 28 },
  { x: 55.1,  y: 77.5,  size: 28 },
  { x: 26.44, y: 42.69, size: 28 },
];

const BASS_V2_SAMPLES: Record<number, string> = {
  0: "bass-low-c.mp3",
  1: "bass-g.mp3",
  2: "bass-high-c.mp3",
};

function OrbitBassV2({ ensemble = false, embedded = false }: { ensemble?: boolean; embedded?: boolean }) {
  const [hitId, setHitId] = useState<string | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [lastSample, setLastSample] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const assetBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

  async function playNote(idx: number) {
    const sampleFile = BASS_V2_SAMPLES[idx] || "bass-low-c.mp3";
    const fullPath = `${assetBase}/tutorial_v2/sounds/${sampleFile}`;
    setLastSample(sampleFile);
    setLastError(null);

    const audio = getAudioElement(fullPath);
    audio.volume = 0.9;

    try {
      await audio.play();
      setAudioUnlocked(true);
    } catch (e: any) {
      const msg = e?.message || String(e);
      console.error("Bass audio failed:", fullPath, msg);
      setLastError(msg);

      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = (window as any).__audioCtx || new Ctx();
        await ctx.resume();
        (window as any).__audioCtx = ctx;

        setTimeout(async () => {
          try {
            await audio.play();
            setAudioUnlocked(true);
            setLastError(null);
          } catch (e2: any) {
            setLastError(e2?.message || "Still blocked");
          }
        }, 80);
      } catch {}
    }

    const id = `bass-${idx}`;
    setHitId(id);
    setTimeout(() => setHitId((h) => (h === id ? null : h)), 650);
  }

  if (embedded) {
    const NOTE_SIZE = ensemble ? 11 : 10;
    return (
      <>
        {BASS_V2_LAYOUT.map((p, i) => {
          const id = `bass-${i}`;
          const note = BASS_V2_NOTES[i];
          const isHit = hitId === id;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`tp-note absolute ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${NOTE_SIZE}%`,
                height: `${NOTE_SIZE}%`,
                marginLeft: `-${NOTE_SIZE / 2}%`,
                marginTop: `-${NOTE_SIZE / 2}%`,
                fontSize: '9px',
              }}
              aria-label={`Play ${note.name}`}
            >
              {note.name}
            </button>
          );
        })}
      </>
    );
  }

  const cw = ensemble ? 320 : 480;
  return (
    <div className="mx-auto" style={{ width: cw }}>
      <div className="relative aspect-square" style={{ width: cw }}>
        <img
          src={`${assetBase}/tutorial_v2/images/pan-bass-w.jpg`}
          alt="Bass steelpan"
          className="absolute inset-0 w-full h-full rounded-full object-cover"
          style={{ boxShadow: "inset 0 12px 45px rgba(0,0,0,.75), inset 0 -30px 75px rgba(0,0,0,.6)" }}
        />
        {BASS_V2_LAYOUT.map((p, i) => {
          const id = `bass-${i}`;
          const note = BASS_V2_NOTES[i];
          const isHit = hitId === id;
          const size = 11;
          return (
            <button
              key={i}
              onClick={() => playNote(i)}
              className={`absolute flex items-center justify-center rounded-full border text-[10px] font-semibold transition-all active:scale-[0.92] ${isHit ? 'hit' : ''}`}
              style={{
                left: `${p.x}%`, top: `${p.y}%`,
                width: `${size}%`, height: `${size}%`,
                marginLeft: `-${size / 2}%`, marginTop: `-${size / 2}%`,
              }}
            >
              {note.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}


const css = `
/* === Exact visual language from /tutorial_v2/index.html === */
* { box-sizing: border-box; }

html, body { background: #0e0a08; color: #fff; }

main { position: relative; min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px 60px; }

.header { text-align: center; margin-bottom: 24px; }
.header h1 { font-family: 'Times New Roman', serif; font-size: clamp(30px, 5.5vw, 46px); font-weight: 400; letter-spacing: -.02em; }
.header .dot { font-style: italic; color: rgba(253,224,71,.65); }

.controls { display: inline-flex; align-items: center; gap: 8px; }
.btn {
  display: inline-flex; align-items: center; gap: 8px;
  border-radius: 999px; border: 1px solid rgba(255,255,255,.15);
  background: rgba(0,0,0,.4); color: rgba(255,255,255,.75);
  padding: 7px 14px; font-size: 11px; letter-spacing: .3em; text-transform: uppercase;
  cursor: pointer; transition: all .2s ease;
}
.btn:hover { border-color: rgba(253,224,71,.45); color: #fff; }
.btn.active { border-color: rgba(253,224,71,.7); background: rgba(253,224,71,.12); color: rgba(254,243,199,1); }

/* THE BIG STAGE WRAPPER — matches v2 .stage-wrap */
.stage-wrap {
  position: relative;
  width: 100%;
  max-width: 1024px;
  aspect-ratio: 16 / 10;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 768px) {
  .stage-wrap { aspect-ratio: 1; }
}

/* Decorative background elements from v2 */
.bg-deco { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.bg-deco .blob {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  width: 820px; height: 820px; border-radius: 50%;
  background: rgba(253,224,71,.055); filter: blur(52px);
}
.bg-deco .ring1 {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  width: 560px; height: 560px; border-radius: 50%; border: 1px solid rgba(253,224,71,.09);
}
.bg-deco .ring2 {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  width: 760px; height: 760px; border-radius: 50%; border: 1px solid rgba(253,224,71,.045);
}

/* Corner satellite thumbnails — v2's signature navigation */
.satellite {
  position: absolute;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer;
  transition: opacity .2s, transform .2s;
  z-index: 20;
}
.satellite .satellite-img {
  width: 102px; height: 102px;
  border-radius: 50%; overflow: hidden;
  border: 2px solid rgba(255,255,255,.14);
  box-shadow: 0 10px 30px rgba(0,0,0,.4);
  transition: all .2s cubic-bezier(.2,.8,.2,1);
}
@media (min-width: 768px) {
  .satellite .satellite-img { width: 128px; height: 128px; }
}
.satellite:hover .satellite-img {
  border-color: rgba(253,224,71,.55);
  transform: scale(1.04);
}
.satellite .satellite-img img {
  width: 100%; height: 100%; object-fit: cover; opacity: .78; transition: opacity .2s;
}
.satellite:hover .satellite-img img { opacity: 1; }
.satellite .satellite-label {
  font-size: 9.5px; text-transform: uppercase; letter-spacing: .32em;
  color: rgba(255,255,255,.5);
}
.satellite:hover .satellite-label { color: rgba(253,224,71,.85); }

/* Central tp-wrap stage (the beautiful photo + notes) */
.tp-wrap {
  position: relative;
  aspect-ratio: 1;
  z-index: 10;
  animation: tpIn .85s cubic-bezier(.2,.85,.2,1);
}
@keyframes tpIn { from { transform: scale(.94); opacity: 0; filter: blur(6px); } to { transform: scale(1); opacity: 1; filter: blur(0); } }

.tp-glow {
  position: absolute; inset: -22%; z-index: 0;
  background: radial-gradient(circle at 50% 54%, rgba(215,155,55,.28), transparent 58%);
  filter: blur(42px);
  animation: tpGlow 7s ease-in-out infinite;
}
@keyframes tpGlow { 0%,100% { opacity:.55; } 50% { opacity: .95; } }

.tp-img-stack {
  position: relative; width: 100%; height: 100%;
  border-radius: 50%; overflow: hidden;
  box-shadow:
    inset 0 14px 52px rgba(0,0,0,.72),
    inset 0 -32px 90px rgba(0,0,0,.58),
    0 40px 110px -26px rgba(210,150,50,.35),
    0 0 0 1px rgba(255,255,255,.05);
  z-index: 1;
}
.tp-img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; opacity: 0; transition: opacity .2s ease;
}
.tp-img.active { opacity: 1; }

.tp-shine {
  position: absolute; inset: 7% 7% 42% 7%; border-radius: 50%;
  background: radial-gradient(ellipse at 29% 17%, rgba(255,255,255,.18), transparent 60%);
  pointer-events: none; z-index: 2;
}

.tp-notes { position: absolute; inset: 0; z-index: 3; }

/* The note pads — exact v2 .tp-note treatment (small, dark, uniform circles) */
.tp-note {
  position: absolute;
  border-radius: 50%;
  background: rgba(0,0,0,.58);
  color: rgba(255,255,255,.92);
  font-size: 9px;
  font-weight: 600;
  text-shadow: 0 1px 5px rgba(0,0,0,.65);
  border: 1.5px solid rgba(255,255,255,.12);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: transform .28s cubic-bezier(.2,.85,.2,1), background .2s, border-color .2s, box-shadow .4s;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  padding: 0;
  line-height: 1;
}
.tp-note:hover {
  transform: scale(1.1);
  background: color-mix(in oklab, #fde08a 12%, transparent);
  border-color: rgba(253,224,71,.35);
}
.tp-note.hit {
  transform: scale(1.6);
  background: linear-gradient(180deg, #fde08a, #d49a2c);
  color: #1a0f00;
  border-color: rgba(255,230,160,.9);
  box-shadow: 0 0 0 2px rgba(255,255,255,.2), 0 0 68px 14px rgba(251,191,36,.58);
  z-index: 10;
  animation: tpRipple .65s ease-out;
}
@keyframes tpRipple {
  0% { box-shadow: 0 0 0 2px rgba(255,255,255,.2), 0 0 68px 14px rgba(251,191,36,.58); }
  100% { box-shadow: 0 0 0 2px rgba(255,255,255,.2), 0 0 0 72px rgba(251,191,36,0); }
}

/* Mobile bottom pill bar — exact v2 .pan-toggler */
.pan-toggler {
  display: none;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 18px;
}
.pan-toggler button {
  display: inline-flex; align-items: center; gap: 6px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(0,0,0,.42);
  color: rgba(255,255,255,.72);
  padding: 5px 11px 5px 7px;
  font-size: 10px;
  letter-spacing: .22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all .2s;
}
.pan-toggler button.active {
  border-color: rgba(253,224,71,.65);
  background: rgba(253,224,71,.13);
  color: rgba(254,243,199,1);
}
.pan-toggler button .pt-thumb {
  width: 18px; height: 18px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(255,255,255,.22);
  flex-shrink: 0;
}
.pan-toggler button .pt-thumb img { width: 100%; height: 100%; object-fit: cover; }

@media (max-width: 768px) {
  .pan-toggler { display: flex; }
  .satellite { display: none !important; }
}

/* Close button */
.tutorial-close {
  position: fixed;
  top: 18px;
  right: 18px;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  color: rgba(255,255,255,.5);
  background: rgba(0,0,0,.38);
  border: 1px solid rgba(255,255,255,.1);
  backdrop-filter: blur(10px);
  cursor: pointer;
  z-index: 200;
  transition: all .2s ease;
}
.tutorial-close:hover {
  color: #fff;
  border-color: rgba(255,255,255,.28);
  background: rgba(0,0,0,.6);
}

/* Hide all site shell (nav + footer) for pure v2 standalone tutorial experience.
   Using body class because SiteNav is rendered as a previous sibling in the root layout. */
body.tutorial-page header,
body.tutorial-page nav,
body.tutorial-page footer {
  display: none !important;
}

/* === Mobile improvements for header controls (All/Solo must be clearly on top) === */
@media (max-width: 768px) {
  main {
    padding-top: 12px;
    padding-bottom: 20px;
  }
  .header {
    margin-bottom: 8px;
  }
  .header h1 {
    font-size: 22px;
    line-height: 1.1;
  }
  .controls {
    gap: 6px;
    margin-top: 6px;
  }
  .btn {
    padding: 6px 12px;
    font-size: 10px;
    letter-spacing: 0.2em;
  }

  /* Bottom mobile bar with 4 items (3 solo + All) next to each other */
  .pan-toggler {
    gap: 4px;
  }
  .pan-toggler button {
    padding: 4px 8px 4px 5px;
    font-size: 9px;
  }
  .pan-toggler button .pt-thumb {
    width: 16px;
    height: 16px;
  }

  /* Top-placed menu (Tenor/Cello/Bass/All) right under the header controls */
  .pan-top-menu {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .pan-top-menu button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.16);
    background: rgba(0,0,0,.42);
    color: rgba(255,255,255,.72);
    padding: 5px 10px 5px 6px;
    font-size: 10px;
    letter-spacing: .2em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all .2s;
  }
  .pan-top-menu button.active {
    border-color: rgba(253,224,71,.65);
    background: rgba(253,224,71,.13);
    color: rgba(254,243,199,1);
  }
  .pan-top-menu button .pt-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,.22);
    flex-shrink: 0;
  }
  .pan-top-menu button .pt-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
`;
